import type { NavigationContainerRef } from '@react-navigation/native';
import { ADD_PRODUCT_UNAUTHORIZED_MESSAGE } from '../../CONSTANTS';
import store from '../../redux/Store';
import { AppSnackbar } from '../../services/AppSnackbar';
import { isEmployeeAdminRole } from '../../utils/roles';
import {
  consumeQueuedDeepLinkHref,
  queueDeepLinkFromNotification,
  queueDeepLinkHref,
} from './deepLinkQueue';
import {
  extractDeepLinkHrefFromNotification,
  parseDeepLink,
  type DeepLinkAction,
} from './parseDeepLink';

type RootNav = NavigationContainerRef<any>;
type RoleReader = () => string | null | undefined;

let navigationRef: RootNav | null = null;
let flushPromise: Promise<boolean> | null = null;
let lastHandledHref = '';
let lastHandledAt = 0;

const defaultReadEmployeeRole: RoleReader = () =>
  store.getState()?.auth?.inApp?.account?.employeeInfo?.bp_employee_role;

let readEmployeeRole: RoleReader = defaultReadEmployeeRole;

export const setDeepLinkRootNavigationRef = (ref: RootNav | null) => {
  navigationRef = ref;
};

export const setDeepLinkRoleReader = (reader: RoleReader | null) => {
  readEmployeeRole = reader ?? defaultReadEmployeeRole;
};

const isDuplicateHref = (href: string): boolean => {
  const now = Date.now();
  if (lastHandledHref === href && now - lastHandledAt < 2000) {
    return true;
  }
  lastHandledHref = href;
  lastHandledAt = now;
  return false;
};

export const canFlushDeepLinkNavigation = (): boolean => {
  if (!navigationRef?.isReady()) return false;
  const root = navigationRef.getRootState?.();
  return !!root?.routes?.some(route => route.name === 'App');
};

const navigateAddProduct = (): boolean => {
  if (!canFlushDeepLinkNavigation() || !navigationRef) return false;

  const role = readEmployeeRole();
  if (role == null || String(role).trim() === '') {
    return false;
  }
  if (!isEmployeeAdminRole(role)) {
    AppSnackbar.show(ADD_PRODUCT_UNAUTHORIZED_MESSAGE, 'error');
    return true;
  }

  navigationRef.navigate('App', {
    screen: 'NewProduct',
    params: { source: 'deeplink' },
  });
  return true;
};

const navigateHome = (): boolean => {
  if (!canFlushDeepLinkNavigation() || !navigationRef) return false;
  navigationRef.navigate('App', {
    screen: 'DrawerNavigation',
    params: { screen: 'Home' },
  });
  return true;
};

const navigateBackOfficeTab = (
  tab: string,
  params?: Record<string, unknown>,
): boolean => {
  if (!canFlushDeepLinkNavigation() || !navigationRef) return false;
  navigationRef.navigate('App', {
    screen: 'BackOffice',
    params: params ? { screen: tab, params } : { screen: tab },
  });
  return true;
};

export const navigateDeepLinkAction = (action: DeepLinkAction): boolean => {
  try {
    switch (action.kind) {
      case 'add_product':
        return navigateAddProduct();
      case 'inventory':
        return navigateBackOfficeTab('Inventory');
      case 'billings':
        return navigateBackOfficeTab('Account');
      case 'employees':
        return navigateBackOfficeTab('People', { screen: 'Staff' });
      case 'customers':
        return navigateBackOfficeTab('People', { screen: 'Customers' });
      case 'reports':
        return navigateBackOfficeTab('Reports');
      case 'dashboard':
        return navigateBackOfficeTab('Dashboard');
      case 'login':
      case 'home':
        return navigateHome();
      default:
        return false;
    }
  } catch (error) {
    console.log('Deep link navigation failed:', error);
    return false;
  }
};

export const handleIncomingDeepLinkUrl = async (
  href: string,
): Promise<boolean> => {
  const action = parseDeepLink(href);
  if (!action) return false;
  if (isDuplicateHref(action.href)) return true;

  const navigated = navigateDeepLinkAction(action);
  if (navigated) return true;

  await queueDeepLinkHref(action.href);
  const retried = navigateDeepLinkAction(action);
  if (retried) {
    await consumeQueuedDeepLinkHref();
  }
  return true;
};

export const handleDeepLinkNotificationPress = async (
  data: unknown,
): Promise<boolean> => {
  const href = extractDeepLinkHrefFromNotification(data);
  if (!href) return false;
  return handleIncomingDeepLinkUrl(href);
};

export const flushPendingDeepLink = async (): Promise<boolean> => {
  if (flushPromise) return flushPromise;

  flushPromise = (async () => {
    const href = await consumeQueuedDeepLinkHref();
    if (!href) return false;
    const action = parseDeepLink(href);
    if (!action) return false;
    const navigated = navigateDeepLinkAction(action);
    if (!navigated) {
      await queueDeepLinkHref(href);
    }
    return navigated;
  })();

  try {
    return await flushPromise;
  } finally {
    flushPromise = null;
  }
};

export { queueDeepLinkFromNotification, queueDeepLinkHref };
